import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  FormControl,
  Select as MUISelect,
  MenuItem,
  ListSubheader,
  TextField,
  InputAdornment,
  IconButton,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

export interface SearchableSelectProps<T = any> {
  value: any;
  onChange: (value: any) => void;
  onBlur?: () => void;
  options: T[];
  idKey?: string;
  labelKey?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  sx?: SxProps<Theme>;
  menuMaxHeight?: number;
  emptyValue?: any;
}

const defaultSelectSx: SxProps<Theme> = {
  height: "42px",
  borderRadius: "8px",
  backgroundColor: "#fbfcfe",
  fontSize: "13.5px",
  fontFamily: "Poppins",
  color: "#1e2433",
  "& fieldset": {
    borderColor: "#e2e8f0",
    transition: "all 0.2s ease",
  },
  "&:hover fieldset": {
    borderColor: "#b8c0d8",
  },
  "&.Mui-focused fieldset": {
    borderColor: "#5d78ff",
    borderWidth: "1.5px",
  },
  "&.Mui-disabled": {
    backgroundColor: "#f1f5f9 !important",
    cursor: "not-allowed !important",
    "& fieldset": {
      borderColor: "#e2e8f0 !important",
    },
    "&:hover fieldset": {
      borderColor: "#e2e8f0 !important",
    },
    "& .MuiSelect-select": {
      color: "#94a3b8 !important",
      WebkitTextFillColor: "#94a3b8 !important",
      cursor: "not-allowed !important",
    },
    "& .MuiSelect-icon": {
      color: "#cbd5e1 !important",
      cursor: "not-allowed !important",
    },
  },
};

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  onBlur,
  options = [],
  idKey = "Id",
  labelKey = "label",
  placeholder = "",
  searchPlaceholder = "Search...",
  disabled = false,
  error = false,
  helperText,
  sx,
  menuMaxHeight = 280,
  emptyValue = 0,
}) => {
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus search input when menu opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setSearchText("");
    }
  }, [open]);

  const filteredOptions = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return options || [];
    return (options || []).filter((item: any) =>
      String(item?.[labelKey] ?? "")
        .toLowerCase()
        .includes(query)
    );
  }, [options, searchText, labelKey]);

  const selectedItem = useMemo(() => {
    if (value === null || value === undefined || value === emptyValue) {
      return null;
    }
    return (options || []).find((item: any) => String(item?.[idKey]) === String(value));
  }, [options, value, idKey, emptyValue]);

  const handleChange = (e: SelectChangeEvent<any>) => {
    const val = e.target.value;
    onChange(val);
  };

  return (
    <FormControl
      fullWidth
      size="small"
      error={error}
      disabled={disabled}
      sx={{ cursor: disabled ? "not-allowed" : "default" }}
    >
      <MUISelect
        open={open}
        onOpen={() => {
          if (!disabled) setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
          onBlur?.();
        }}
        onBlur={onBlur}
        value={value ?? emptyValue}
        disabled={disabled}
        onChange={handleChange}
        displayEmpty
        renderValue={() => {
          if (!selectedItem) {
            return (
              <span
                className={`font-[Poppins] text-[13.5px] ${
                  disabled ? "text-[#94a3b8]" : "text-gray-400"
                }`}
              >
                {placeholder}
              </span>
            );
          }
          return (
            <span
              className={`font-[Poppins] text-[13.5px] ${
                disabled ? "text-[#94a3b8]" : "text-[#1e2433]"
              }`}
            >
              {selectedItem[labelKey]}
            </span>
          );
        }}
        sx={{
          ...defaultSelectSx,
          ...sx,
          ...(disabled
            ? {
                backgroundColor: "#f1f5f9 !important",
                cursor: "not-allowed !important",
                "& fieldset": {
                  borderColor: "#e2e8f0 !important",
                },
                "&:hover fieldset": {
                  borderColor: "#e2e8f0 !important",
                },
                "& .MuiSelect-select": {
                  color: "#94a3b8 !important",
                  WebkitTextFillColor: "#94a3b8 !important",
                  cursor: "not-allowed !important",
                },
                "& .MuiSelect-icon": {
                  color: "#cbd5e1 !important",
                  cursor: "not-allowed !important",
                },
              }
            : {}),
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: {
              maxHeight: menuMaxHeight,
              borderRadius: "10px",
              border: "1px solid #edf0f7",
              boxShadow: "0 10px 30px rgba(38, 46, 77, 0.12)",
              overflowY: "auto",
              mt: 0.5,
            },
          },
          MenuListProps: {
            autoFocusItem: false,
            sx: { pt: 0 },
          },
        }}
      >
        {/* Sticky Search Header (Styled like Image 2) */}
        <ListSubheader
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#ffffff",
            px: 1.5,
            py: 1,
            lineHeight: "normal",
            borderBottom: "1px solid #edf0f7",
          }}
        >
          <TextField
            inputRef={searchInputRef}
            placeholder={searchPlaceholder}
            variant="outlined"
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      fontSize: 17,
                      color: "#94a3b8",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchText("");
                      searchInputRef.current?.focus();
                    }}
                    edge="end"
                    sx={{ padding: "2px" }}
                  >
                    <CloseIcon
                      sx={{
                        fontSize: 14,
                        color: "#94a3b8",
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "38px",
                borderRadius: "8px",
                backgroundColor: "#fbfcfe",
                fontSize: "13px",
                fontFamily: "Poppins",
                color: "#1e2433",
                "& fieldset": {
                  borderColor: "#e2e8f0",
                  transition: "all 0.2s ease",
                },
                "&:hover fieldset": {
                  borderColor: "#b8c0d8",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#5d78ff",
                  borderWidth: "1.5px",
                },
              },
            }}
          />
        </ListSubheader>

        {/* Filtered Options List */}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((item: any) => (
            <MenuItem
              key={String(item[idKey])}
              value={item[idKey]}
              sx={{ fontFamily: "Poppins", fontSize: "13.5px" }}
            >
              {item[labelKey]}
            </MenuItem>
          ))
        ) : (
          <MenuItem
            disabled
            sx={{
              fontFamily: "Poppins",
              fontSize: "13px",
              color: "#94a3b8",
              justifyContent: "center",
              py: 2,
            }}
          >
            No results found
          </MenuItem>
        )}

        {/* Hidden MenuItem for selected value if not in filtered list (prevents MUI out-of-range warning) */}
        {value !== null &&
          value !== undefined &&
          value !== emptyValue &&
          !filteredOptions.some((item: any) => String(item[idKey]) === String(value)) && (
            <MenuItem value={value} sx={{ display: "none" }}>
              {selectedItem?.[labelKey] || value}
            </MenuItem>
          )}
      </MUISelect>

      {error && helperText && (
        <span className="text-red-500 text-[11px] mt-1 ml-1 font-[Poppins]">
          {helperText}
        </span>
      )}
    </FormControl>
  );
};

export default SearchableSelect;
